import React from 'react';
import {InputText} from 'primereact/inputtext';
import {Dropdown, DropdownChangeParams} from 'primereact/dropdown';
import Editor, {loader} from "@monaco-editor/react";
import {Button} from "primereact/button";
import {ProductTemplateService} from "../../api/ProdTemplateApi";

declare function PF(widgetVar: string): any

type TemplateTable = {
    id: bigint,
    refName: string,
    title: string,
}

type ConfiguratorProps = {
    templates: Array<TemplateTable>
}

type ConfiguratorState = {
    selectedTemplate: TemplateTable | null,
    templateConfigJson: string | undefined,
    template: any,
}

loader.init().then((monaco) => {
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        schemas: [
            {
                uri: "http://easas/product_template_schema_v1.json", // id of the first schema
                fileMatch: ["*"], // associate with our model
                schema: {
                    type: "object",
                    properties: {
                        attributes: {
                            type: "array",
                            title: "Template attributes",
                            items: { "$ref": "#/$defs/templateAttribute" },
                            default: [{referenceName: "reference_name", displayName: "Display Name", type: "INSTANT"}]
                        },
                        config: {
                            type: "object",
                            title: "General template settings",
                            $ref: "#/$defs/templateConfig"
                        },
                        parameterTemplates: {
                            type: "array",
                            title: "Parameter section templates",
                            items: { "$ref": "#/$defs/parameterTemplate" },
                        },
                    },

                    $defs: {
                        templateAttribute :{
                            type: "object",
                            valid: false,
                            properties: {
                                referenceName:{
                                    type: "string",
                                    description: "Unique table identifier",
                                    default: "attribute_name"
                                },
                                displayName:{
                                    type: "string",
                                    description: "User friendly attribute name",
                                    default: "Attribute name"
                                },
                                description:{
                                    type: "string",
                                    description: "Attribute description",
                                    default: "Short description"
                                },
                                type:{
                                    type: "string",
                                    description: "Attribute data type",
                                    enum: ["LONG", "DOUBLE", "STRING", "DATETIME", "INSTANT", "TIME", "TIMESPAN", "FOREIGN_KEY", "USER_SIGNATURE", "PARAMETERS"]
                                },
                                unit: {
                                    type: "string",
                                    description: "Data unit (for numerical)",
                                    default: "KWh"
                                },
                                shortenText: {
                                    type: "boolean",
                                    description: "Use dialog for STRING attribute",
                                    default: false
                                },
                                decimalPlaces: {
                                    type: "integer",
                                    description: "Number of decimal places to show (Only applies to decimal/double values)",
                                    default: 2
                                },
                                defaultValue: {
                                    type: "object",
                                    description: "Default value for this attribute",
                                    default: null
                                },
                                autoFillTime: {
                                    type: "boolean",
                                    description: "Auto fill date and/or time with current datetime on entry creation",
                                    default: false
                                },
                                selectionValues: {
                                    type: "array",
                                    description: "Predefined selection values. Currently works only with STRING",
                                    default: ["Selection 1", "Selection 2"]
                                },
                                locked: {
                                    type: "boolean",
                                    description: "If true nobody can edit column,it is update programmatically e.g. from Python script",
                                    default: false,
                                },
                                requiredPermission: {
                                    type: "string",
                                    description: " Required permission to edit this column. If user does not have required permission, column will be locked",
                                    default: "PRODUCT_TEMPLATE_ADMIN"
                                },
                                aggregationType: {
                                    type: "string",
                                    description: "Total row aggregation type",
                                    enum: ["SUM", "MEAN", "COUNT"]
                                },
                                evaluationExpression: {
                                    type: "string",
                                    description: "Expression to calculate this column value",
                                    default: "$some_attr1 + $some_attr3 * $some_attr_2"
                                },
                                dateDisplayPattern: {
                                    type: "string",
                                    description: "If the column type is Date, this field specifies how it is formatted",
                                    default: "yyyy-MM-dd HH:mm"
                                },
                                foreignTemplate: {
                                    type: "string",
                                    description: "If the column type is FOREIGN_KEY, this field specifies the foreign table safe name",
                                    default: "some_other_table_name"
                                },
                                bgHighlightRanges: {
                                    type: "array",
                                    description: "Highlight values according to ranges higher priority than highlightColor. Only works for LONG and DOUBLE attribute types",
                                    default: [],
                                    items: { "$ref": "#/$defs/highlightRange" },
                                },
                                highlightColor: {
                                    type: "object",
                                    description: "Highlight color for column.",
                                    properties: {
                                        color: {
                                            type: "string",
                                            description: "Text color (CSS color)",
                                            default: "inherit"
                                        },
                                        bgColor: {
                                            type: "string",
                                            description: "Background color (CSS color)",
                                            default: "inherit"
                                        }
                                    },
                                    default: {
                                        color: "inherit",
                                        bgColor: "inherit"
                                    },
                                },
                                appliedParameterTemplates: {
                                    type: "array",
                                    description: "List of parameter template sections applied to this column on entry creation",
                                },
                            },
                        },
                        templateConfig: {
                            type: "object",
                            properties: {
                                toStringFormatter: {
                                    type: "string",
                                    description: "Entry to string conversion. Use attribute prefixed with $. Predefined attributes #key, #meta, #start, #end",
                                    default: "Batch No.#key of period #start - #end [$productType]"
                                },
                                startAttribute: {
                                    type: "string",
                                    description: "Attribute reference name which describes entry start moment",
                                    default: "date_actual_start"
                                },
                                stopAttribute: {
                                    type: "string",
                                    description: "Attribute reference name describes entry end moment",
                                    default: "date_actual_end"
                                },
                                rowsPerPage: {
                                    type: "integer",
                                    description: "Default rows per page",
                                    default: 10
                                },
                                relations: {
                                    type: "array",
                                    description: "Describes template relation via attribute join",
                                    default: [ {
                                        templateReference: "reference_to_other_table",
                                        joinAttributes: {
                                            thisTableAttr: "otherTableAttr",
                                            thisTableAttr3: "otherTableAttr6"
                                        }
                                    }]
                                },
                                loadingType: {
                                    type: "string",
                                    description: "Template loading type",
                                    enum: ["EVERYTHING", "FIXED_PERIOD", "RANGE"]
                                },
                                loadingConfig: {
                                    type: "object",
                                    description: "Template loading config",
                                    $ref: "#/$defs/periodLoadingConfig"
                                },
                                sortingStrategy: {
                                    type: "string",
                                    description: "Sorting strategy of template",
                                    enum: ["KEY_AND_META_KEY", "CREATION_DATE", "ADVANCED"],
                                    default: "KEY_AND_META_KEY"
                                },
                                sortingStrategyOptions: {
                                    type: "object",
                                    description: "Sorting strategy options",
                                    properties: {
                                        columns: {
                                            type: "array",
                                            description: "Columns to sort by",
                                            items: { "$ref": "#/$defs/columnSortOptions" },
                                        }
                                    },
                                    default: {
                                        "columns": [
                                            {
                                                "column": "col_ref",
                                            }
                                        ]
                                    }
                                },
                                displayTotalRow: {
                                    type: "boolean",
                                    description: "Do display total row of table",
                                    default: false
                                },
                                allowGenerateReport: {
                                    type: "boolean",
                                    description: "Allow generation email reports",
                                    default: false
                                },
                                allowRowAddition: {
                                    type: "boolean",
                                    description: "Allow entry creation",
                                    default: true
                                },
                                allowRowDeletion: {
                                    type: "boolean",
                                    description: "Allow row removal",
                                    default: true
                                },
                                allowRowEdit: {
                                    type: "boolean",
                                    description: "Allow row modification",
                                    default: true
                                },
                                timezoneId: {
                                    type: "string",
                                    description: "Timezone ID",
                                    default: "Europe/Vilnius"
                                },
                            },
                        },
                        periodLoadingConfig: {
                            type: "object",
                            properties: {
                                truncateStartUnit: {
                                    type: "string",
                                    description: "Align start date to for loading time selection",
                                    enum: ["MINUTES", "HOURS", "DAYS", "WEEKS", "MONTHS", "YEARS"]
                                },
                                periodUnit: {
                                    type: "string",
                                    description: "Time period unit for time loading types",
                                    enum: ["MINUTES", "HOURS", "DAYS", "WEEKS", "MONTHS", "YEARS"]
                                },
                                period: {
                                    type: "integer",
                                    description: "Number of periods to load",
                                    default: 1
                                },
                                offset: {
                                    type: "integer",
                                    description: "Offset of load window",
                                    default: 1
                                },
                                offsetUnit: {
                                    type: "string",
                                    description: "Offset unit of load windows",
                                    enum: ["MINUTES", "HOURS", "DAYS", "WEEKS", "MONTHS", "YEARS"]
                                },
                            },
                        },
                        parameterTemplate: {
                            type: "object",
                            properties: {
                                sectionTitle: {
                                    type: "string",
                                    description: "Section title",
                                    default: "Title"
                                },
                                parameters: {
                                    type: "array",
                                    description: "Parameter definitions",
                                    items: { "$ref": "#/$defs/parameterDefinition" },
                                    default: [
                                        {
                                            "name": "param1",
                                            "type": "NUMBER",
                                            "options": {
                                                "defaultUnit": "rpm",
                                            }
                                        }
                                    ]
                                },
                            },
                        },

                        parameterDefinition: {
                            type: "object",
                            properties: {
                                name: {
                                    type: "string",
                                    description: "Parameter name",
                                    default: "param1"
                                },
                                type: {
                                    type: "string",
                                    description: "Parameter type",
                                    enum: ["TEXT", "NUMBER", "LINK", "DATE", "FILE", "IMAGE", "MOMENT_NUMBER"],
                                },
                                options: {
                                    type: "object",
                                    description: "Parameter options",
                                    properties: {
                                        defaultUnit: {
                                            type: "string",
                                            description: "Default unit for number parameter",
                                        }
                                    }
                                }
                            }
                        },

                        highlightRange: {
                            type: "object",
                            description: "NUMBER or DOUBLE attribute highlight range",
                            properties: {
                                min: {
                                    type: "object",
                                    description: "Min value of range",
                                    $ref: "#/$defs/highlightRangeValue"
                                },
                                max: {
                                    type: "object",
                                    description: "Max value of range",
                                    $ref: "#/$defs/highlightRangeValue"
                                },
                                color: {
                                    type: "string",
                                    description: "Range text color (CSS color)",
                                    default: "inherit"
                                },
                                bgColor: {
                                    type: "string",
                                    description: "Range background color (CSS color)",
                                    default: "inherit"
                                }
                            }
                        },
                        highlightRangeValue: {
                            type: "object",
                            description: "Highlight range value",
                            properties: {
                                value: {
                                    type: "number",
                                    description: "Range value",
                                    default: 0
                                },
                                inclusive: {
                                    type: "boolean",
                                    description: "Is range value inclusive",
                                    default: false
                                }
                            }
                        },

                        columnSortOptions: {
                            type: "object",
                            description: "Column sort options",
                            properties: {
                                column: {
                                    type: "string",
                                    description: "Range value",
                                    default: "col_ref"
                                },
                                order: {
                                    type: "string",
                                    enum: ["ASCENDING", "DESCENDING"],
                                    description: "Is range value inclusive",
                                    default: "DESCENDING"
                                }
                            }
                        }
                    }
                }
            }
        ]
    });
    }).catch((error) =>
        console.error("An error occurred during initialization of Monaco: ", error)
    );

class ProductTemplatesConfiguratorJson extends React.Component<ConfiguratorProps, ConfiguratorState> {
    private readonly templateApi = ProductTemplateService()

    constructor(props: ConfiguratorProps) {
        super(props);

        this.state = {
            selectedTemplate: null,
            templateConfigJson: undefined,
            template: {
                displayName: "New Template Display Name",
                safeName: "company_some_table_name",
                keyColumn: "key_column_safe_name",
            },
        }
    }

    showSuccess = (message: string) => { PF('globalGrowl').renderMessage({"summary":"Success", "detail": message, "severity":"info"}); }
    showError = (message: string) => { PF('globalGrowl').renderMessage({ "summary":"Error", "detail": message, "severity":"error"}); }
    showInfo = (message: string) => { PF('globalGrowl').renderMessage({"summary":"Info", "detail": message, "severity":"info"}); }

    handleClear = () => {
        this.setState( {selectedTemplate: null, template: { }, templateConfigJson: undefined})
        this.showInfo("Cleared")
    }

    onTemplateChange = (e: DropdownChangeParams) => { this.setState({selectedTemplate: e.target.value}, this.reloadConfig)}
    onDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => { this.setState(state => {state.template.displayName = e.target.value}, this.forceUpdate)}
    onRefNameChange = (e: React.ChangeEvent<HTMLInputElement>) => { this.setState(state => {state.template.safeName = e.target.value},  this.forceUpdate)}
    onKeyAttrChange = (e: React.ChangeEvent<HTMLInputElement>) => { this.setState(state => {state.template.keyColumn = e.target.value}, this.forceUpdate)}
    onMetaKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => { this.setState(state => {state.template.metaKeyColumn = e.target.value},  this.forceUpdate)}

    reloadConfig = () => {
        this.templateApi.getConfig(this.state.selectedTemplate?.refName)
            .then(t => this.setState({
                template: t,
                templateConfigJson: JSON.stringify(t.data, null, 2)
            }));
    }


    updateConfig = () => {
        let ref = this.state.selectedTemplate?.refName

        let err = this.checkJsonAndMergeConfig()
        if(err) return;

        this.templateApi.updateConfig(ref, this.state.template)
            .then(r => {
                if(r.status == undefined){
                    this.showSuccess(`Template "${r.displayName}" updated`)
                }else{
                    this.showError(r.message)
                }
            })
    }
    
    checkJsonAndMergeConfig = () => {
        try{
            this.state.template.data = JSON.parse(this.state.templateConfigJson!!)
        }catch (ex:any){
            this.showError("JSON Error: " +  ex.message)
            return ex
        }
    }

    createTemplate = () => {
        let err = this.checkJsonAndMergeConfig()
        if(err) return;

        this.templateApi.create(this.state.template)
            .then(r => {
                if(r.status == undefined){
                    this.showSuccess(`Template "${r.displayName}" created`)
                }else{
                    this.showError(r.message)
                }
            })

        this.showSuccess("Created template")
    }

    downloadProdTemplate = () =>  {
            let err = this.checkJsonAndMergeConfig()
            if(err) return;

            let template = this.state.template;
            let fileName = this.state.selectedTemplate?.refName || "no_name_prod_template" + "__" + new Date().toISOString();
            let json = JSON.stringify(template, null, 2);
            let blob = new Blob([json],{type:'application/json'});
            let href = URL.createObjectURL(blob);
            let link = document.createElement('a');
            link.href = href;
            link.download = fileName + ".json";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
    }


    render(){
        const isTableSelected = this.state.selectedTemplate !== null
        let submitButton;

        if(isTableSelected){
            submitButton = <Button label="Save" icon="pi pi-check" onClick={this.updateConfig} iconPos="right" className="p-m-2" style={{width: "50%"}}/>
        }else {
            submitButton = <Button label="Create" icon="pi pi-plus" onClick={this.createTemplate} iconPos="right" className="p-button-success p-m-2" style={{width: "50%"}}/>
        }

        return (
            <div style={{width: "100%", padding: "0 1rem 0 1rem"}}>


                <div className="p-fluid p-formgrid p-grid">
                    {/* ===== Table selection ======= */}
                    <div className="p-field p-col-11">
                        <label htmlFor="type">Template</label>
                        <Dropdown value={this.state.selectedTemplate}
                                  options={this.props.templates}
                                  filter showClear filterBy="title"
                                  onChange={this.onTemplateChange}
                                  optionLabel="title"
                                  placeholder="Select a template"
                        />
                    </div>
                    {/* ===== Table selection ======= */}
                    <div className="p-field p-col-1">
                        <label>Download</label>
                        <Button icon="pi pi-download" style={{width:"100%"}} onClickCapture={this.downloadProdTemplate}/>
                    </div>
                    <div className="p-field p-col-12">
                        <label>Display name</label>
                        <InputText placeholder="Display name" value={this.state.template?.displayName} onChange={this.onDisplayNameChange} className="p-inputtext-sm"/>
                    </div>
                    <div className="p-field p-col-4">
                        <label>Id</label>
                        <InputText placeholder="Not initialized" value={this.state.template?.id} className="p-inputtext-sm" disabled={true}/>
                    </div>
                    <div className="p-field p-col-4">
                        <label>Date created</label>
                        <InputText placeholder="Date created" value={this.state.template?.dateCreated} className="p-inputtext-sm" disabled={true}/>
                    </div>
                    <div className="p-field p-col-4">
                        <label>Date modified</label>
                        <InputText placeholder="Date modified" value={this.state.template?.dateModified} className="p-inputtext-sm" disabled={true}/>
                    </div>
                    <div className="p-field p-col-12">
                        <label>Reference name</label>
                        <InputText placeholder="Reference name (is auto prefixed with company)" value={this.state.template?.safeName} onChange={this.onRefNameChange} className="p-inputtext-sm" readOnly={isTableSelected}/>
                    </div>
                    <div className="p-field p-col-6">
                        <label>Key attribute</label>
                        <InputText placeholder="Key attribute" value={this.state.template?.keyColumn} onChange={this.onKeyAttrChange} className="p-inputtext-sm" disabled={false}/>
                    </div>
                    <div className="p-field p-col-6">
                        <label>Secondary key <i>(meta)</i> attribute</label>
                        <InputText placeholder="Secondary key" value={this.state.template?.metaKeyColumn} onChange={this.onMetaKeyChange} className="p-inputtext-sm" disabled={false}/>
                    </div>
                </div>

                <div className="p-d-flex p-jc-center">
                    <div style={{width: "100%"}}>
                        <Editor
                            height="400px"
                            defaultLanguage="json"
                            value={this.state.templateConfigJson}
                            onChange={(e) => this.setState({templateConfigJson: e})}
                            theme="vs-dark"
                        />
                    </div>
                </div>

                <div className="p-d-flex p-jc-between">
                    {submitButton}
                    <Button label="Clear" icon="pi pi-trash" onClick={this.handleClear} iconPos="right" className="p-button-danger p-m-2" style={{width: "50%"}}/>
                </div>
            </div>
        );
    }
}


export default ProductTemplatesConfiguratorJson;


